import * as React from "react";
import type { Omittable } from "../lib/props";

/**
 * CSS selector matching the wrapper elements that portaled overlays (Base UI
 * popovers, menus, selects, dialogs) attach to `document.body`. Used by
 * `useOutsideClick` so a click inside a portaled menu/listbox isn't treated as
 * "outside" the cell that opened it.
 *
 * Base UI tags every popup surface with a `data-slot` ending in `-popup`; the
 * ARIA roles catch the rest.
 */
export const PORTALED_OVERLAY_SELECTOR =
  "[data-slot$='-popup'], [role='dialog'], [role='menu'], [role='listbox']";

export type UseOutsideClickOptions = Omittable<{
  /**
   * Element whose subtree counts as "inside." Clicks within this ref are
   * ignored. Typed against `Node["contains"]` so a real element ref
   * (`useRef<HTMLDivElement | null>`) satisfies it — a `(node: unknown)`
   * parameter would be contravariantly incompatible with every DOM node.
   */
  ref: React.RefObject<{ contains?: (node: Node | null) => boolean } | null>;
  /** When false, no listener is attached. */
  active: boolean;
  /** Called when a click lands outside `ref` and outside any portaled overlay. */
  onOutside: (event: MouseEvent) => void;
  /**
   * Optional extra predicate. Return `true` to treat the click as inside
   * (ignore it). Useful for native pickers that fire clicks on `document.body`.
   */
  shouldIgnore?: (target: HTMLElement) => boolean;
}>;

export function useOutsideClick({
  ref,
  active,
  onOutside,
  shouldIgnore,
}: UseOutsideClickOptions): void {
  React.useEffect(() => {
    if (!active) return;
    // A portaled overlay counts as inside only when it floats ABOVE the
    // layer that holds `ref` — a menu or listbox the editor opened. The
    // overlay that CONTAINS the ref (the drawer or dialog hosting the cell)
    // is the cell's own ground: a click elsewhere in it is an outside click,
    // or an editor inside a dialog could never be dismissed by clicking away.
    const inFloatingOverlay = (target: HTMLElement) => {
      const overlay = target.closest(PORTALED_OVERLAY_SELECTOR);
      if (overlay === null) return false;
      const own = ref.current;
      return !(own instanceof Node && overlay.contains(own));
    };
    const isInside = (target: HTMLElement) =>
      Boolean(ref.current?.contains?.(target)) ||
      inFloatingOverlay(target) ||
      Boolean(shouldIgnore?.(target));

    // Containment is decided at POINTERDOWN, while the pressed element is
    // still in the DOM. A click on another inline editor's trigger swaps that
    // trigger for an input inside its own handler, so by the time the click
    // reaches `document` its target is detached and can be "inside" nothing —
    // read at click time, that press would never dismiss this layer, and two
    // cells would sit in edit mode at once. The pointerdown saw the trigger
    // where it stood.
    let downInside: boolean | null = null;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      downInside = target ? isInside(target) : null;
    };
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const inside = downInside;
      downInside = null;
      if (inside !== null) {
        if (!inside) onOutside(event);
        return;
      }
      // No pointerdown reached this listener before the click. Either this
      // is the click that ACTIVATED the layer — React flushed the activation,
      // and this attach effect with it, synchronously inside the click, after
      // its pointerdown had already passed — or it is a keyboard click. The
      // activating click often lands on an element its own handler unmounted
      // (the cell's display trigger), so a detached target here is that click
      // and never an outside one.
      if (!target.isConnected) return;
      if (isInside(target)) return;
      onOutside(event);
    };
    // Both listeners run in the CAPTURE phase. A press target that owns its
    // click — a card row stopping propagation so the card press does not open
    // the issue, a cell trigger guarding a row press — stops the bubble at
    // React's root, and a bubble-phase listener here would never hear the
    // click that should dismiss this layer: two cells would sit in edit mode
    // at once. Capture sees every click before any handler can stop it.
    // Attaching mid-click (the activating click, see above) keeps working:
    // the capture phase at `document` has already passed when the trigger's
    // handler activates the layer, so that click never reaches this listener.
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [active, onOutside, ref, shouldIgnore]);
}
